import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PortalIcon from './PortalIcon';
import {
  formatDate,
  formatMoney,
  orderPaymentLabel,
  orderPaymentPending,
  orderProcessLabel,
  statusTone,
} from '../lib/portal';

function Badge({ tone, children }) {
  return <span className={`portal-badge portal-badge--${tone}`}>{children}</span>;
}

export default function PortalDashboard() {
  const { authRequestJson } = useAuth();
  const navigate = useNavigate();
  const [cliente, setCliente] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    Promise.all([authRequestJson('/portal/me'), authRequestJson('/portal/pedidos?take=200')])
      .then(([me, orderResponse]) => {
        if (!active) return;
        setCliente(me?.data?.cliente || null);
        setOrders(Array.isArray(orderResponse?.data) ? orderResponse.data : []);
      })
      .catch((requestError) => {
        if (active) setError(requestError.message || 'No se pudo cargar el resumen.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [authRequestJson]);

  const metrics = useMemo(() => {
    const pendingProcess = orders.filter((order) => orderProcessLabel(order) === 'Pendiente de procesar').length;
    const pendingPayment = orders
      .filter(orderPaymentPending)
      .reduce((total, order) => total + Number(order.valorViaje || 0), 0);
    return { total: orders.length, pendingProcess, pendingPayment };
  }, [orders]);

  const recent = orders.slice(0, 5);
  const displayName = cliente?.nombre || cliente?.razonSocial || 'tu cuenta';

  if (loading) {
    return <div className="portal-app"><div className="portal-wrap portal-loading">Cargando tu operación…</div></div>;
  }

  return (
    <div className="portal-app">
      <div className="portal-wrap">
        {error ? <div className="portal-error-panel"><strong>No pudimos actualizar el resumen.</strong>{error}</div> : null}

        <section className="portal-origin-banner" role="note">
          <span className="portal-origin-banner-icon"><PortalIcon name="pin" size={21} /></span>
          <div className="portal-origin-banner-copy">
            <p className="portal-kicker">Regla de cobertura</p>
            <h2>Origen de despacho</h2>
            <p>
              Las cotizaciones automáticas están habilitadas para orígenes en CABA y AMBA. Si despachás desde el interior,
              necesitás una tarifa especial configurada para tu cliente y esa localidad de origen.
            </p>
          </div>
          <Link className="portal-btn portal-btn--outline portal-btn--small" to="/soporte">Contactar soporte <PortalIcon name="arrow" size={15} /></Link>
        </section>

        <section className="portal-hero">
          <div>
            <p className="portal-kicker">Centro de operaciones / {cliente?.tiendaNubeId ? 'Tienda vinculada' : 'Cuenta en preparación'}</p>
            <h1>Hola, {displayName}.</h1>
            <p>Todo lo que necesitás para seguir tus envíos y coordinar el próximo retiro está en un solo lugar.</p>
            <span className="portal-session-chip"><i /> Operación ESA activa</span>
          </div>
          <div className="portal-hero-art" aria-hidden="true"><div className="portal-hero-orbit" /></div>
        </section>

        <section className="portal-metrics" aria-label="Resumen de pedidos">
          <article className="portal-metric">
            <span className="portal-metric-label">Pedidos recibidos</span>
            <strong className="portal-metric-value portal-number">{metrics.total}</strong>
            <span className="portal-metric-note">Pedidos sincronizados desde Tiendanube</span>
          </article>
          <article className="portal-metric portal-metric--accent">
            <span className="portal-metric-label">Pendientes de procesar</span>
            <strong className="portal-metric-value portal-number">{metrics.pendingProcess}</strong>
            <span className="portal-metric-note">Listos para pasar a Saadis</span>
          </article>
          <article className="portal-metric">
            <span className="portal-metric-label">Monto pendiente de pago</span>
            <strong className="portal-metric-value">{formatMoney(metrics.pendingPayment)}</strong>
            <span className="portal-metric-note">Fletes aún no abonados en ESA</span>
          </article>
        </section>

        <section className="portal-section">
          <div className="portal-section-title">
            <div><p className="portal-kicker">Actividad reciente</p><h2>Últimos pedidos</h2></div>
            <Link className="portal-btn portal-btn--outline portal-btn--small" to="/pedidos">Ver todos <PortalIcon name="arrow" size={15} /></Link>
          </div>
          <div className="portal-card portal-table-card">
            {recent.length === 0 ? (
              <div className="portal-empty"><strong>Aún no hay pedidos recibidos.</strong><p>Cuando Tiendanube confirme un pago, el pedido aparecerá acá automáticamente.</p></div>
            ) : (
              <div className="portal-table-wrap">
                <table className="portal-table">
                  <thead><tr><th>Pedido</th><th>Destino</th><th>Estado</th><th>Flete</th><th>Actualizado</th></tr></thead>
                  <tbody>
                    {recent.map((order) => (
                      <tr key={order.id} data-clickable="true" onClick={() => navigate(`/pedidos/${order.id}`)}>
                        <td><strong>#{order.tiendaNubeOrderNumber || order.tiendaNubeOrderId}</strong><span className="portal-muted">{order.contactName || order.contactEmail || 'Cliente Tiendanube'}</span></td>
                        <td>{order.localidadDestino || 'Destino pendiente'}<span className="portal-muted">CP {order.codigoPostalDestino || '—'}</span></td>
                        <td><Badge tone={statusTone(orderProcessLabel(order))}>{orderProcessLabel(order)}</Badge></td>
                        <td><Badge tone={statusTone(order.cobroEstado)}>{orderPaymentLabel(order)}</Badge></td>
                        <td>{formatDate(order.actualizadoEn)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>

        <section className="portal-section">
          <div className="portal-section-title"><div><p className="portal-kicker">Siguiente paso</p><h2>Mové tu operación</h2></div></div>
          <div className="portal-action-grid">
            <Link className="portal-action-card" to="/cotizar">
              <span className="portal-action-icon"><PortalIcon name="stack" size={20} /></span>
              <div><h3>Cotizar un envío</h3><p>Calculá un nuevo envío con el tarifario activo de ESA.</p></div>
            </Link>
            <Link className="portal-action-card" to="/colectas/nueva">
              <span className="portal-action-icon"><PortalIcon name="truck" size={20} /></span>
              <div><h3>Solicitar una colecta</h3><p>Seleccioná pedidos pagados y coordiná el retiro.</p></div>
            </Link>
            <Link className="portal-action-card" to="/conectar-tienda">
              <span className="portal-action-icon">
                <PortalIcon name="paper" size={20} />
              </span>

              <div>
                <h3>
                  {cliente?.tiendaNubeId
                    ? 'Administrar tienda conectada'
                    : 'Conectar mi tienda'}
                </h3>

                <p>
                  {cliente?.tiendaNubeId
                    ? 'Revisá la vinculación y sincronizá nuevamente tus pedidos de Tiendanube.'
                    : 'Vinculá tu tienda de Tiendanube para importar y gestionar tus pedidos desde ESA.'}
                </p>
              </div>
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}
